import React from "react";
import { pullCampaignOffices } from "../../../store/api/calls";
import { updateOffice, insertOffice, setCampaignOfficesLoading, setCampaignOfficesError } from "../../../store/actions";
import { connect } from "react-redux";
import DataTable from "../../theme/tables/DataTable";
import BaseModal from "../../theme/modals/BaseModal";
import BaseForm from "../../theme/forms/BaseForm";
import Moment from "moment";
import api from "../../../store/api/api";
import { LinearProgress } from "@mui/material";

const ERROR_TIMEOUT = 10000;

class OfficesTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editModalOpen: false,
      editModalErrorMsg: null,
      deleteModalOpen: false,
      editingInstance: {},
      showCampaignEditSection: true,
      formInputs: [],
      geocodeError: false,
      geoErrorMessage: "",
      isLoading: false,
      operationError: null,
      uberOrganizations: [],
    };
  }

  componentDidMount() {
    pullCampaignOffices();
    api.get("uber-organizations/").then((res) => {
      this.setState({ uberOrganizations: res.data });
    }).catch((err) => console.log(err));
  }

  updateOrCreateOffice = (data) => {
    this.setState({ isLoading: true, operationError: null });

    if (Object.keys(data).includes("id")) {
      return api
        .patch(`offices/${data.id}/`, data)
        .then((res) => {
          this.props.updateOffice(res.data);
          this.setState({ isLoading: false });
          return res.data;
        })
        .catch((err) => {
          console.log(err);
          const errorMessage = err.response?.data?.error || "Failed to update office";
          this.setState({ 
            isLoading: false, 
            operationError: errorMessage 
          });
          setTimeout(() => {
            this.setState({ operationError: null });
          }, ERROR_TIMEOUT);
          throw err;
        });
    } else {
      return api.post("offices/", data).then((res) => {
        this.props.insertOffice(res.data);
        this.setState({ isLoading: false });
        return res.data;
      }).catch((err) => {
        console.log(err);
        const errorMessage = err.response?.data?.error || "Failed to create office";
        this.setState({ 
          isLoading: false, 
          operationError: errorMessage 
        });
        setTimeout(() => {
          this.setState({ operationError: null });
        }, ERROR_TIMEOUT);
        throw err;
      });
    }
  };

  _filterKeysForOfficeDataTable = () => {
    return this.props.offices.map((d) => ({
      name: d.name,
      address: d.addressline1 + " " + d.addressline2,
      city: d.city,
      state: d.statecode,
      zip: d.zip5,
      uber_org: d.uber_organization?.name || "-",
      created_on: new Moment(d.created_at).format("MM/DD/YY"),
      _created_on: new Moment(d.created_at),
      _data: d,
    }));
  };

  _generateFormInputs = (creating = false, instance = {}) => {
    return [
      {
        type: "text",
        title: "Office Name",
        dataKey: "name",
        placeholder: "Pleasantville",
        value: instance.name || "",
      },
      {
        type: "text",
        title: "Address Line 1",
        dataKey: "addressline1",
        placeholder: "123 Main",
        value: instance.addressline1 || "",
      },
      {
        type: "text",
        title: "Address Line 2",
        dataKey: "addressline2",
        placeholder: "Suite 100",
        value: instance.addressline2 || "",
      },
      {
        type: "text",
        title: "City",
        dataKey: "city",
        placeholder: "Pleasantville",
        value: instance.city || "",
      },
      {
        type: "text",
        title: "State",
        dataKey: "statecode",
        placeholder: "Two Character State Code",
        value: instance.statecode || "",
      },
      {
        type: "Zip",
        title: "ZipCode",
        dataKey: "zip5",
        placeholder: creating
          ? "At least five characters, with at least one number and letter"
          : "Leave blank if you don't want to change this.",
        value: instance.zip5 || "",
      },
      {
        type: "select",
        title: "Uber Organization",
        dataKey: "uber_organization",
        value: instance.uber_organization?.id ?? instance.uber_organization ?? "",
        options: [
          { value: "", display: "None" },
          ...this.state.uberOrganizations.map((o) => ({ value: o.id, display: `${o.name} (${o.uber_org_id})` })),
        ],
      },
    ];
  };

  onOpenEditModal = (modelInstance = {}, creating = false) => {
    this.setState({
      formInputs: this._generateFormInputs(creating, modelInstance),
      editModalOpen: true,
      editingInstance: modelInstance,
    });
  };

  onCloseEditModal = () => {
    this.setState({
      editModalOpen: false,
      editingInstance: {},
      editModalErrorMsg: null,
    });
  };

  onOpenDeleteModal = (modelInstance = {}) => {
    this.setState({ deleteModalOpen: true, editingInstance: modelInstance });
  };

  onCloseDeleteModal = () => {
    this.setState({ deleteModalOpen: false, editingInstance: {} });
  };

  handleUpdateCreateSubmission = (data) => {
    const id = this.state.editingInstance.id;
    if (id) data = { ...data, id };

    if (data.uber_organization) {
      data = { ...data, uber_organization: parseInt(data.uber_organization, 10) };
    } else {
      data = { ...data, uber_organization: null };
    }

    data = { ...data, campaign: this.props.focusedCampaign.id };
    this.geocode(data)
      .then((res) => {
        data.lat = res[0].geometry.location.lat;
        data.lon = res[0].geometry.location.lng;
        this.updateOrCreateOffice(data).then(() => {
          this.onCloseEditModal();
        }).catch((err) => {
          this.setState({ 
            editModalErrorMsg: this.state.operationError || "Failed to save office"
          });
        });
      })
      .catch((err) => {
        this.setState({ 
          editModalErrorMsg: err.message,
          geocodeError: true, 
          geoErrorMessage: err.message 
        });
        setTimeout(() => this.setState({ geocodeError: false }), 10000);
      });
  };

  handleRemoveSubmission = () => {
    const id = this.state.editingInstance.id;
    this.updateOrCreateOffice({ is_active: false, id })
      .then(this.onCloseDeleteModal)
      .catch((err) => {
        // Error handling is done in updateOrCreateOffice, modal will stay open
        console.error("Failed to remove office:", err);
      });
  };

  geocode = (data) => {
    let address_obj = { ...data };
    // only send address info.
    delete address_obj.campaign;
    delete address_obj.name;
    delete address_obj.uber_organization;
    let address = Object.values(address_obj).join(" ");

    return api
      .get("/geocode", { address })
      .then((res) => {
        if (res.data.results.length > 0) {
          return res.data.results;
        } else {
          return Promise.reject(new Error("No Matches Found"));
        }
      })
      .catch(() => {
        return Promise.reject(
          new Error(
            "Unable To Create Office. Please check address for errors and try again."
          )
        );
      });
  };

  render() {
    const { focusedCampaign } = this.props;
    const { showCampaignEditSection } = this.state;

    return (
      <div>
        {showCampaignEditSection && (
          <div className="top-section-large">
            <div className="row">
              <div className="column column-40">
                <h2>{focusedCampaign.name}</h2>
                <p>{focusedCampaign.description}</p>
              </div>
              <div className="column column-20">
                <div className="row">
                  <div className="column">
                    <div
                      className="button secondary-milligram"
                      onClick={this.props.handleEditFocusedCampaign}
                    >
                      Edit
                    </div>
                  </div>
                  <div className="column">
                    <div
                      className="button danger"
                      onClick={this.props.handleRemoveFocusedCampaign}
                    >
                      Deactivate Campaign
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="button-table-top-wrapper">
          <a 
            className={`button ${this.state.isLoading ? 'disabled' : ''}`} 
            onClick={() => !this.state.isLoading && this.onOpenEditModal({}, true)}
          >
            {this.state.isLoading ? 'Processing...' : '+ Add Office'}
          </a>
        </div>

        {this.state.operationError && (
          <div className="danger-text flx center" style={{ margin: '10px 0' }}>
            {this.state.operationError}
          </div>
        )}

        {this.state.geocodeError && (
          <div className="danger-text flx center" style={{ margin: '10px 0' }}>
            {this.state.geoErrorMessage}
          </div>
        )}

        <DataTable
          data={this._filterKeysForOfficeDataTable()}
          title="Offices"
          actions={[
            {
              title: "edit",
              icon: "fa-edit",
              action: (modelInstance) => this.onOpenEditModal(modelInstance),
            },
            {
              title: "Remove",
              icon: "fa-minus-square",
              action: this.onOpenDeleteModal,
            },
          ]}
        />

        <BaseModal
          open={this.state.editModalOpen}
          onClose={this.onCloseEditModal}
          center
        >
          {this.state.isLoading && <LinearProgress />}
          <BaseForm
            inputs={this.state.formInputs}
            onCancel={this.onCloseEditModal}
            onSubmit={this.handleUpdateCreateSubmission}
            isLoading={this.state.isLoading}
          />
          {!!this.state.editModalErrorMsg && (
            <div className="danger-text flx center">
              {this.state.editModalErrorMsg}
            </div>
          )}
        </BaseModal>

        <BaseModal
          open={this.state.deleteModalOpen}
          onClose={this.onCloseDeleteModal}
          center
        >
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to remove the office{" "}
              {this.state.editingInstance.name}?
            </p>
            {this.state.operationError && (
              <div className="danger-text flx center" style={{ margin: '10px 0' }}>
                {this.state.operationError}
              </div>
            )}
            <div className="button-wrapper">
              <div 
                className={`button ${this.state.isLoading ? 'disabled' : ''}`} 
                onClick={!this.state.isLoading ? this.handleRemoveSubmission : undefined}
              >
                {this.state.isLoading ? 'Removing...' : 'Remove'}
              </div>
              <div
                className="button button-outline"
                onClick={this.onCloseDeleteModal}
              >
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>
      </div>
    );
  }
}

export default connect(null, { updateOffice, insertOffice, setCampaignOfficesLoading, setCampaignOfficesError })(OfficesTab); 